import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { User, ChatConversation } from '../types';
import { api } from '../services/apiService';
import ConversationList from '../components/chat/ConversationList';
import ConversationView from '../components/chat/ConversationView';
import NewConversationModal from '../components/chat/NewConversationModal';
import ChatSearchBar from '../components/chat/ChatSearchBar';

interface ChatViewProps {
  currentUser: User;
}

// Polling adaptatif :
//   - 3s tant qu'il y a de l'activité (nouveau message détecté)
//   - bascule à 10s après 5 polls sans changement
//   - bascule à 30s après 10 polls supplémentaires sans changement
//   - retour immédiat à 3s dès qu'un changement est détecté ou que l'user
//     ouvre une conv / envoie un message
const POLL_FAST_MS = 3000;
const POLL_MEDIUM_MS = 10000;
const POLL_SLOW_MS = 30000;
const POLLS_BEFORE_MEDIUM = 5;
const POLLS_BEFORE_SLOW = 10;

const ChatView: React.FC<ChatViewProps> = ({ currentUser }) => {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);

  // Charger la liste des users de la compagnie une seule fois (pour la modale "nouvelle conv")
  useEffect(() => {
    if (!currentUser.companyId) return;
    api.getCompanyUsers(currentUser.companyId)
      .then(setCompanyUsers)
      .catch(() => setCompanyUsers([]));
  }, [currentUser.companyId]);

  const pollTimerRef = useRef<number | null>(null);
  const docVisibleRef = useRef<boolean>(typeof document !== 'undefined' ? !document.hidden : true);
  // Signature de la liste précédente — pour détecter "rien n'a changé"
  const lastSignatureRef = useRef<string>('');
  const idleCountRef = useRef<number>(0);

  // Calcule une signature de la liste pour détecter un changement (nouveau
  // message reçu, nouvelle conv, last_read_at modifié, etc.)
  const signatureOf = (list: ChatConversation[]): string =>
    list.map(c => `${c.id}:${c.last_message_at || ''}:${c.unread_count}`).join('|');

  // ----- Charger la liste des conversations -----
  const refreshList = useCallback(async (resetIdle = false) => {
    try {
      const list = await api.chatListConversations();
      const sig = signatureOf(list);
      const changed = sig !== lastSignatureRef.current;
      lastSignatureRef.current = sig;
      if (changed || resetIdle) {
        idleCountRef.current = 0;
      } else {
        idleCountRef.current += 1;
      }
      setConversations(list);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Erreur de chargement');
    } finally {
      setLoadingList(false);
    }
  }, []);

  // Premier chargement
  useEffect(() => {
    refreshList(true);
  }, [refreshList]);

  // ----- Polling adaptatif (auto-rescheduling via setTimeout récursif) -----
  useEffect(() => {
    let cancelled = false;

    const scheduleNext = () => {
      if (cancelled) return;
      const idle = idleCountRef.current;
      const delay = idle >= POLLS_BEFORE_SLOW
        ? POLL_SLOW_MS
        : idle >= POLLS_BEFORE_MEDIUM
          ? POLL_MEDIUM_MS
          : POLL_FAST_MS;
      pollTimerRef.current = window.setTimeout(async () => {
        if (cancelled) return;
        if (docVisibleRef.current) {
          await refreshList();
        }
        scheduleNext();
      }, delay);
    };
    scheduleNext();

    return () => {
      cancelled = true;
      if (pollTimerRef.current) window.clearTimeout(pollTimerRef.current);
    };
  }, [refreshList]);

  // ----- Pause polling quand l'onglet n'est pas actif -----
  useEffect(() => {
    const onVis = () => {
      docVisibleRef.current = !document.hidden;
      if (!document.hidden) {
        // Retour sur l'onglet → reset cadence rapide + refresh immédiat
        idleCountRef.current = 0;
        refreshList(true);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refreshList]);

  // ----- Action : créer ou ouvrir une conversation 1-to-1 -----
  const handleStartDirect = async (targetUserId: string) => {
    try {
      const { id } = await api.chatCreateDirect(targetUserId);
      setShowNewModal(false);
      await refreshList();
      setSelectedId(id);
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de la création de la conversation');
    }
  };

  // ----- Action : créer un groupe -----
  const handleCreateGroup = async (name: string, participantIds: string[], isPrivate: boolean) => {
    const { id } = await api.chatCreateGroup(name, participantIds, isPrivate);
    setShowNewModal(false);
    await refreshList();
    setSelectedId(id);
  };

  // ----- Action : ouvrir une conversation depuis la liste -----
  const handleSelect = async (convId: string) => {
    setSelectedId(convId);
    idleCountRef.current = 0; // reset cadence rapide à l'interaction
    // Marquer comme lue immédiatement côté serveur ; le refresh suivant
    // remettra le compteur non-lu à 0 dans la liste.
    try {
      await api.chatMarkRead(convId);
      // Optimistic update local du badge non-lu
      setConversations(prev => prev.map(c =>
        c.id === convId ? { ...c, unread_count: 0 } : c
      ));
    } catch { /* non-bloquant */ }
  };

  // ----- Callback envoi message : reset cadence rapide -----
  const handleMessageSent = useCallback(() => {
    idleCountRef.current = 0;
    refreshList(true);
  }, [refreshList]);

  const selectedConv = useMemo(
    () => conversations.find(c => c.id === selectedId) || null,
    [conversations, selectedId]
  );

  // Liste des users de la compagnie (hors moi) pour la modale "nouvelle conv"
  const eligibleUsers = useMemo(
    () => companyUsers.filter(u => u.id !== currentUser.id),
    [companyUsers, currentUser.id]
  );

  return (
    <div className="flex h-full overflow-hidden">
      {/* Panneau gauche : liste des conversations */}
      <aside className="w-[320px] flex-shrink-0 border-r border-[rgba(100,140,210,0.2)] flex flex-col bg-[rgba(15,30,60,0.4)]">
        <div className="px-4 py-3 border-b border-[rgba(100,140,210,0.2)] flex items-center justify-between">
          <h2 className="text-sm font-bold text-[#e0eaff]">Conversations</h2>
          <button
            type="button"
            onClick={() => setShowNewModal(true)}
            className="orion-settings-primary-btn text-white text-xs font-bold px-3 py-1.5 rounded-lg"
            title="Nouvelle conversation"
          >
            +
          </button>
        </div>

        {/* Barre de recherche FULLTEXT */}
        <div className="p-3 border-b border-[rgba(100,140,210,0.1)]">
          <ChatSearchBar
            onSelectResult={(convId) => {
              setSelectedId(convId);
              idleCountRef.current = 0;
            }}
          />
        </div>

        {error && (
          <div className="m-3 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-xs text-rose-300">
            {error}
          </div>
        )}

        {loadingList ? (
          <div className="p-6 text-center text-xs text-[#546a96]">Chargement…</div>
        ) : conversations.length === 0 ? (
          <div className="p-6 text-center text-xs text-[#546a96]">
            Aucune conversation pour l'instant.<br />
            Cliquez sur <strong>+</strong> pour en démarrer une.
          </div>
        ) : (
          <ConversationList
            conversations={conversations}
            selectedId={selectedId}
            currentUserId={currentUser.id}
            onSelect={handleSelect}
          />
        )}
      </aside>

      {/* Panneau droit : vue de la conversation sélectionnée */}
      <main className="flex-1 flex flex-col bg-[rgba(10,20,40,0.3)]">
        {selectedConv ? (
          <ConversationView
            conversation={selectedConv}
            currentUser={currentUser}
            onMessageSent={handleMessageSent}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-[#546a96] text-sm">
            Sélectionnez une conversation à gauche, ou créez-en une nouvelle.
          </div>
        )}
      </main>

      {showNewModal && (
        <NewConversationModal
          users={eligibleUsers}
          onClose={() => setShowNewModal(false)}
          onSelectUser={handleStartDirect}
          onCreateGroup={handleCreateGroup}
        />
      )}
    </div>
  );
};

export default ChatView;
